import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  useTheme,
  Tooltip,
  Link,
  Skeleton,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';

import { 
  Edit, 
  Add, 
  Delete, 
  Close, 
  Save, 
  Phone, 
  FormatSize, 
  LocationOn, 
  Description,
  Link as LinkIcon,
  FormatColorText,
  Star,
  TouchApp,
  Settings,
  Visibility,
  Email,
  ContentCopy,
} from '@mui/icons-material';
import ModalMAIQ from 'app/components/ModalMAIQ';
import SettingsIcon from '@mui/icons-material/Settings';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import StarIcon from '@mui/icons-material/Star';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useDealerContentSettings } from 'app/context/DealerContentSettingsContext';
import { CTAButton, DealerContentSetting, Contact, DaysOfService, MarketingData } from 'app/apiService/dealerContentSettings/dealerContentSettingsApiService';
import TagSelector from 'app/components/TagSelector';
import apiService from 'app/apiService';
import { formatToDealerTime, getDealerTimezoneFromStorage } from 'utils/dateUtils';
import moment from 'moment-timezone';
import {
  HeaderMakeSelect,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderMakeSelect';
import {
  HeaderSearchableSelectMAIQ,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderSearchableSelectMAIQ';
import {
  MakeItem,
} from 'app/components/ReusableUIComponents/LowLevelComponents/makeSearchUtils';
import { fetchMakes } from 'app/pages/MyautoiqSettingsPage/apiService/myautoiqSettingsApiService';
import { fetchMyautoiqSegmentSettings } from 'app/pages/MyautoiqSegmentSettingsComponent/data/myautoiqSegmentSettingsDataService';

interface DealerSettingsTabProps {}

type BasicSettingRow =
  | { kind: 'structured'; field: keyof DealerContentSetting; label: string }
  | { kind: 'website'; label: string };

const BASIC_SETTINGS_ROWS: BasicSettingRow[] = [
  { kind: 'website', label: 'Website URL' },
  { kind: 'structured', field: 'logo_bar_url', label: 'Logo Bar URL' },
  { kind: 'structured', field: 'address', label: 'Address' },
  { kind: 'structured', field: 'phone', label: 'Phone' },
  { kind: 'structured', field: 'standard_disclaimer_text', label: 'Standard Disclaimer Text' },
  { kind: 'structured', field: 'footer_url', label: 'Footer URL' },
  { kind: 'structured', field: 'footer_image_url', label: 'Footer Image URL' },
];

const DealerSettingsTab: React.FC<DealerSettingsTabProps> = (): JSX.Element => {
  const theme = useTheme();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  
  // Check if we're in NewAIAvatarLayout context
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const selectedDealerId = selectedDealer.dealer || '';

  // Use the context
  const {
    dealerContentSettings,
    loading,
    error,
    getDealerContentSettings,
    updateDealerContentSettings,
    createDealerContentSettings,
    addCtaButton,
    updateCtaButton,
    deleteCtaButton,
    getDefaultDealerContentSettings,
  } = useDealerContentSettings();

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<{ [key: string]: string }>(
    {},
  );
  const [editModalTitle, setEditModalTitle] = useState('');
  const [editModalField, setEditModalField] = useState<string>('');
  const [editModalType, setEditModalType] = useState<string>('');
  const [editModalOriginalValue, setEditModalOriginalValue] = useState<string>('');
  const [editModalError, setEditModalError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CTA Modal State
  const [ctaModalOpen, setCtaModalOpen] = useState(false);
  const [ctaModalMode, setCtaModalMode] = useState<'add' | 'edit'>('add');
  const [ctaModalData, setCtaModalData] = useState<CTAButton>({
    id: '',
    name: '',
    url: '',
    type: 'SRP',
    tags: [],
  });
  const [ctaSubmitting, setCtaSubmitting] = useState(false);

  // Color Picker Modal State
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerField, setColorPickerField] = useState<string>('');
  const [colorPickerValue, setColorPickerValue] = useState<string>('');
  const [colorPickerTitle, setColorPickerTitle] = useState<string>('');
  const [colorPickerError, setColorPickerError] = useState<string>('');
  const [colorPickerHistory, setColorPickerHistory] = useState<string[]>([]);

  // Preview Modal State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewSettings, setPreviewSettings] = useState<DealerContentSetting | null>(null);
  const [showInlinePreview, setShowInlinePreview] = useState(false);

  // Contact Modal State
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalType, setContactModalType] = useState<'primary' | 'invoice' | 'secondary'>('primary');
  const [contactModalData, setContactModalData] = useState<Contact>({});
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // Days of Service Modal State
  const [daysOfServiceModalOpen, setDaysOfServiceModalOpen] = useState(false);
  const [daysOfServiceData, setDaysOfServiceData] = useState<{
    sunday?: DaysOfService;
    monday?: DaysOfService;
    tuesday?: DaysOfService;
    wednesday?: DaysOfService;
    thursday?: DaysOfService;
    friday?: DaysOfService;
    saturday?: DaysOfService;
  }>({});
  const [daysOfServiceSubmitting, setDaysOfServiceSubmitting] = useState(false);

  // Active carlist makes (same source as Myautoiq Settings brands dropdown)
  const [availableMakes, setAvailableMakes] = useState<MakeItem[]>([]);
  const [makesLoading, setMakesLoading] = useState(false);

  // Per-make enablement based on whether MyAutoIQ has DEALER_SEGMENTS_CONFIG for that make.
  const [makeSegmentsAvailability, setMakeSegmentsAvailability] = useState<Record<string, boolean>>({});
  const [makeSegmentsLoading, setMakeSegmentsLoading] = useState(false);

  const selectedMakeItems = useMemo((): MakeItem[] => {
    const selectedMakes = dealerContentSettings?.make;
    const selectedIds = Array.isArray(selectedMakes)
      ? selectedMakes.map((id: string) => String(id).trim())
      : [];
    if (selectedIds.length === 0 || availableMakes.length === 0) {
      return [];
    }
    const selectedSet = new Set(selectedIds);
    return availableMakes.filter(make => selectedSet.has(String(make.make_id).trim()));
  }, [dealerContentSettings?.make, availableMakes]);

  // Timezone State
  const [timezones, setTimezones] = useState<Array<{ name: string; offset?: number; offsetString?: string; abbrev?: string }>>([]);
  const [timezonesLoading, setTimezonesLoading] = useState(false);

  const timezoneOptions = useMemo(
    () =>
      timezones.map(timezone => ({
        value: timezone.name,
        label: timezone.name,
      })),
    [timezones],
  );

  // Marketing Data Modal State
  const [marketingDataModalOpen, setMarketingDataModalOpen] = useState(false);
  const [marketingData, setMarketingData] = useState<MarketingData>({});
  const [marketingDataSubmitting, setMarketingDataSubmitting] = useState(false);
  
  // Validation errors for marketing data
  const [householdIncomeError, setHouseholdIncomeError] = useState<string>('');
  const [purchasingPowerError, setPurchasingPowerError] = useState<string>('');

  // Load dealer content settings
  useEffect(() => {
    if (selectedDealerId) {
      getDealerContentSettings(selectedDealerId);
    }
  }, [selectedDealerId]);


  // Validate marketing data when modal opens or data changes
  useEffect(() => {
    if (marketingDataModalOpen) {
      const householdIncome = marketingData.ideal_customer?.household_income;
      const purchasingPower = marketingData.ideal_customer?.purchasing_power;
      
      // Validate household income: between 50000 and 1000000
      if (householdIncome !== undefined) {
        if (householdIncome < 50000 || householdIncome > 1000000) {
          setHouseholdIncomeError('Household income must be between $50,000 and $1,000,000');
        } else {
          setHouseholdIncomeError('');
        }
      } else {
        setHouseholdIncomeError('');
      }
      
      // Validate purchasing power: between 459 and 820
      if (purchasingPower !== undefined) {
        if (purchasingPower < 459 || purchasingPower > 820) {
          setPurchasingPowerError('Purchasing power must be between 459 and 820');
        } else {
          setPurchasingPowerError('');
        }
      } else {
        setPurchasingPowerError('');
      }
    }
  }, [marketingDataModalOpen, marketingData.ideal_customer?.household_income, marketingData.ideal_customer?.purchasing_power]);

  // Load active carlist makes (GET /api/v2/carlist/makes)
  useEffect(() => {
    const loadMakes = async () => {
      try {
        setMakesLoading(true);
        const response = await fetchMakes();
        const makesList = response?.body || [];
        const normalized: MakeItem[] = (Array.isArray(makesList) ? makesList : [])
          .map((make: any) => ({
            make_id: String(make.make_id || ''),
            make_name: make.make_name || '',
            manufacturer_id: String(make.manufacturer_id || ''),
            manufacturer_name: make.manufacturer_name || '',
          }))
          .filter((make: MakeItem) => make.make_id);
        setAvailableMakes(normalized);
      } catch (error) {
        console.error('Error loading makes:', error);
        setAvailableMakes([]);
      } finally {
        setMakesLoading(false);
      }
    };

    loadMakes();
  }, []);

  // Load Timezones
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        setTimezonesLoading(true);
        const response = await apiService.get('api/v2/timezones');
        // Handle different response structures - timezones are array of objects with name property
        let timezonesArray: Array<{ name: string; offset?: number; offsetString?: string; abbrev?: string }> = [];
        
        if (Array.isArray(response.body)) {
          timezonesArray = response.body;
        } else if (Array.isArray(response.body?.data)) {
          timezonesArray = response.body.data;
        } else if (Array.isArray(response.body?.body)) {
          timezonesArray = response.body.body;
        }
        
        // Ensure we have valid timezone objects with name property
        if (timezonesArray.length > 0 && typeof timezonesArray[0] === 'object' && timezonesArray[0].name) {
          setTimezones(timezonesArray);
        } else {
          setTimezones([]);
        }
      } catch (error) {
        console.error('Error loading timezones:', error);
        setTimezones([]);
      } finally {
        setTimezonesLoading(false);
      }
    };

    loadTimezones();
  }, []);

  // Handle timezone change - must be before any early returns (Rules of Hooks)
  const handleTimezoneChange = useCallback(async (timezone: string) => {
    if (!dealerContentSettings || !selectedDealerId) return;
    
    // Only send the time_zone field in the payload
    const updatePayload: Partial<DealerContentSetting> = {
      time_zone: timezone || undefined,
    };
    
    await updateDealerContentSettings(selectedDealerId, updatePayload);
  }, [dealerContentSettings, selectedDealerId, updateDealerContentSettings]);

  // Load MyAutoIQ DEALER_SEGMENTS_CONFIG presence per make so we can enable/disable makes in UI.
  useEffect(() => {
    if (!Array.isArray(availableMakes) || availableMakes.length === 0) {
      setMakeSegmentsAvailability({});
      return;
    }

    let cancelled = false;
    const run = async () => {
      setMakeSegmentsLoading(true);
      try {
        const next: Record<string, boolean> = {};
        await Promise.all(
          availableMakes.map(async make => {
            const makeId = String(make.make_id || '').trim();
            const manufacturerId = String(make.manufacturer_id || '').trim();
            if (!makeId || !manufacturerId) {
              next[makeId] = false;
              return;
            }
            try {
              const resp = await fetchMyautoiqSegmentSettings({
                manufacturerId,
                makeId,
              });

              const body = resp?.body ?? resp?.data ?? resp;
              // MyAutoIQ segment settings response shape differs; just treat presence of the config as "exists".
              const exists =
                Boolean(body?._id && body?.type === 'DEALER_SEGMENTS_CONFIG') ||
                (Array.isArray(body) && body?.[0]?.settings?.length > 0);
              next[makeId] = exists;
            } catch (e) {
              next[makeId] = false;
            }
          }),
        );

        if (!cancelled) setMakeSegmentsAvailability(next);
      } finally {
        if (!cancelled) setMakeSegmentsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [availableMakes]);

  // Remove automatic creation of default settings
  // Default settings should only be created when user clicks the "Configure" button

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.primary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view dealer settings
        </Typography>
      </Box>
    );
  }

  // Show skeleton loading state while dealer content settings are being loaded
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Basic Settings Skeleton */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            boxShadow: theme.shadows[2],
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Skeleton variant="circular" width={26} height={26} />
            <Skeleton variant="text" width={150} height={32} />
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} key={item}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    p: 2,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Box>
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Call To Action Buttons Skeleton */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            boxShadow: theme.shadows[2],
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={180} height={32} />
            </Box>
            <Skeleton variant="rounded" width={100} height={36} />
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.primary.main, height: 48 }}>
                  <TableCell sx={{ color: theme.palette.primary.contrastText || 'white', fontWeight: 700, fontSize: 16, border: 0, pl: 3, minWidth: 120 }}>
                    <Skeleton variant="text" width={60} height={20} sx={{ bgcolor: theme.palette.action.hover }} />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.primary.contrastText || 'white', fontWeight: 700, fontSize: 16, border: 0, minWidth: 300 }}>
                    <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: theme.palette.action.hover }} />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.primary.contrastText || 'white', fontWeight: 700, fontSize: 16, border: 0, textAlign: 'center', minWidth: 100 }}>
                    <Skeleton variant="text" width={60} height={20} sx={{ bgcolor: theme.palette.action.hover }} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3].map((item) => (
                  <TableRow key={item}>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} height={20} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Color Settings Skeleton */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            boxShadow: theme.shadows[2],
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={140} height={32} />
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={60} height={40} sx={{ borderRadius: 1 }} />
                  </Box>
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  }

  const openEditModal = (field: string, title: string, value: string) => {
    const isHarmonizedWebsite = field === 'harmonized_website';

    // Check if it's a color field
    if (!isHarmonizedWebsite && getSettingType(field as keyof DealerContentSetting) === 'color') {
      setColorPickerField(field);
      setColorPickerValue(value);
      setColorPickerTitle(title);
      setColorPickerError('');
      // Load color history from localStorage
      const history = JSON.parse(localStorage.getItem('colorPickerHistory') || '[]');
      setColorPickerHistory(history);
      setColorPickerOpen(true);
    } else {
      setEditModalTitle(title);
      setEditModalField(field);
      setEditModalType(
        isHarmonizedWebsite
          ? 'url'
          : getSettingType(field as keyof DealerContentSetting),
      );
      setEditModalOriginalValue(value);
      setEditModalData({ [field]: value });
      setEditModalError('');
      setEditModalOpen(true);
    }
  };

  const handleSave = async () => {
    if (!dealerContentSettings) return;

    // Validation based on field type
    const currentValue = editModalData[editModalField] ?? '';
    const isHarmonizedWebsite = editModalField === 'harmonized_website';

    if (!isHarmonizedWebsite && !currentValue.trim()) {
      setEditModalError('This field cannot be empty');
      return;
    }

    // Additional validation for specific fields
    if (
      (editModalField.includes('url') || isHarmonizedWebsite) &&
      currentValue.trim() &&
      !isValidUrl(currentValue)
    ) {
      setEditModalError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    if (editModalField.includes('phone') && !isValidPhone(currentValue)) {
      setEditModalError('Please enter a valid phone number');
      return;
    }



    setIsSubmitting(true);
    setEditModalError('');
    
    try {
      if (editModalField === 'harmonized_website') {
        await updateDealerContentSettings(selectedDealerId, {
          primary_contact: {
            ...(dealerContentSettings.primary_contact || {}),
            website: currentValue,
          },
        });
      } else {
        const currentField = dealerContentSettings[
          editModalField as keyof DealerContentSetting
        ] as any;
        
        // Only send the edited field in the payload
        const updatePayload: Partial<DealerContentSetting> = {
          [editModalField]: {
            ...currentField,
            value: currentValue,
          },
        };

        await updateDealerContentSettings(selectedDealerId, updatePayload);
      }

      setEditModalOpen(false);
    } catch (error) {
      console.error('Error saving dealer content settings:', error);
      setEditModalError('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditModalOpen(false);
    setEditModalError('');
  };

  // Contact Modal Handlers
  const handleContactModalSave = async () => {
    if (!dealerContentSettings) return;
    setContactSubmitting(true);
    try {
      const contactField = contactModalType === 'primary' ? 'primary_contact' : contactModalType === 'invoice' ? 'invoice_contact' : 'secondary_contact';
      
      // Only send the edited contact field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        [contactField]: contactModalData,
      };
      
      await updateDealerContentSettings(selectedDealerId, updatePayload);
      setContactModalOpen(false);
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleContactModalCancel = () => {
    setContactModalOpen(false);
    setContactModalData({});
  };

  // Copy primary contact to current contact
  const handleCopyFromPrimaryContact = () => {
    if (dealerContentSettings?.primary_contact) {
      setContactModalData({ ...dealerContentSettings.primary_contact });
    }
  };

  // Days of Service Modal Handlers
  const handleDaysOfServiceModalSave = async () => {
    if (!dealerContentSettings) return;
    setDaysOfServiceSubmitting(true);
    try {
      // Only send the days_of_service field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        days_of_service: daysOfServiceData,
      };
      
      await updateDealerContentSettings(selectedDealerId, updatePayload);
      setDaysOfServiceModalOpen(false);
    } catch (error) {
      console.error('Error saving days of service:', error);
    } finally {
      setDaysOfServiceSubmitting(false);
    }
  };

  const handleDaysOfServiceModalCancel = () => {
    setDaysOfServiceModalOpen(false);
    setDaysOfServiceData({});
  };

  // Helper to convert time string to minutes
  const timeToMinutes = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let totalHours = hours;
    if (period === 'PM' && hours !== 12) totalHours += 12;
    if (period === 'AM' && hours === 12) totalHours = 0;
    return totalHours * 60 + minutes;
  };

  // Helper to convert minutes to time string
  const minutesToTime = (minutes: number): string => {
    if (minutes === undefined || minutes === null) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Marketing Data Modal Handlers
  const handleMarketingDataModalSave = async () => {
    if (!dealerContentSettings) return;
    
    // Check for validation errors before saving
    if (householdIncomeError || purchasingPowerError) {
      return;
    }
    
    setMarketingDataSubmitting(true);
    try {
      // Only send the marketing_data field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        marketing_data: marketingData,
      };
      
      await updateDealerContentSettings(selectedDealerId, updatePayload);
      setMarketingDataModalOpen(false);
      setHouseholdIncomeError('');
      setPurchasingPowerError('');
    } catch (error) {
      console.error('Error saving marketing data:', error);
    } finally {
      setMarketingDataSubmitting(false);
    }
  };

  const handleMarketingDataModalCancel = () => {
    setMarketingDataModalOpen(false);
    setMarketingData({});
    setHouseholdIncomeError('');
    setPurchasingPowerError('');
  };

  // Validation helper functions
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidPhone = (phone: string): boolean => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };



  const getFieldIcon = (field: string): JSX.Element => {
    if (field.includes('url')) return <LinkIcon fontSize="small" />;
    if (field.includes('phone')) return <Phone fontSize="small" />;
    if (field.includes('font')) return <FormatSize fontSize="small" />;
    if (field.includes('address')) return <LocationOn fontSize="small" />;
    if (field.includes('disclaimer')) return <Description fontSize="small" />;
    return <Edit fontSize="small" />;
  };

  const getFieldDescription = (field: string): string => {
    const descriptions: { [key: string]: string } = {
      logo_bar_url: 'Enter the URL for your dealership logo image. This will be displayed in the header of your content.',
      address: 'Enter your dealership\'s complete address. This will be used in contact information sections.',
      phone: 'Enter your dealership\'s primary phone number. Use format: (555) 123-4567',
      standard_disclaimer_text: 'Enter the standard disclaimer text that will appear on all your content. This is important for legal compliance.',
      footer_url: 'Enter the URL that will be linked from your footer. This could be your main website or a specific landing page.',
      footer_image_url: 'Enter the URL for your footer image/logo. This will be displayed at the bottom of your content.',
      harmonized_website: 'Enter your dealership website URL. This stays in sync with Dealer Information & URLs.',
      content_font: 'Choose the font family for your main content text. Popular choices include Arial, Roboto, Open Sans.',
      content_font_size: 'Set the font size for your main content. Use numbers like 14, 16, 18, or click the quick options below.',
      disclaimer_font: 'Choose the font family for disclaimer text. Should be readable and professional.',
      disclaimer_font_size: 'Set the font size for disclaimer text. Usually smaller than main content.',
      primary_headline_size: 'Set the font size for primary headlines. Use numbers like 20, 22, 24, or click the quick options below.',
      headline_highlight_font_size: 'Set the font size for highlighted headlines. Use numbers like 16, 18, 20, or click the quick options below.',
    };
    return descriptions[field] || 'Enter the value for this setting.';
  };

  const getFieldPlaceholder = (field: string): string => {
    const placeholders: { [key: string]: string } = {
      logo_bar_url: 'https://example.com/logo.png',
      address: '123 Main Street, City, State 12345',
      phone: '(555) 123-4567',
      standard_disclaimer_text: 'Terms and conditions apply. Subject to availability.',
      footer_url: 'https://example.com',
      footer_image_url: 'https://example.com/footer-logo.png',
      harmonized_website: 'https://www.dealerwebsite.com',
      content_font: 'Roboto, Arial, sans-serif',
      disclaimer_font: 'Arial, sans-serif',
      content_font_size: '16',
      disclaimer_font_size: '14',
      primary_headline_size: '22',
      headline_highlight_font_size: '18',
    };
    return placeholders[field] || 'Enter value...';
  };

  const hasChanges = (): boolean => {
    return editModalData[editModalField] !== editModalOriginalValue;
  };

  // Color Picker Functions
  const validateColor = (color: string): boolean => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(color);
  };

  const handleColorPickerSave = async () => {
    if (!dealerContentSettings) return;

    // Validate color format
    if (!validateColor(colorPickerValue)) {
      setColorPickerError('Please enter a valid hex color (e.g., #FF0000)');
      return;
    }

    setIsSubmitting(true);
    setColorPickerError('');
    
    try {
      const currentField = dealerContentSettings[
        colorPickerField as keyof DealerContentSetting
      ] as any;
      
      // Only send the edited color field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        [colorPickerField]: {
          ...currentField,
          value: colorPickerValue,
        },
      };

      // Always use PUT for updates since we're editing existing settings
      await updateDealerContentSettings(selectedDealerId, updatePayload);

      // Save to color history
      const newHistory = [colorPickerValue, ...colorPickerHistory.filter(c => c !== colorPickerValue)].slice(0, 10);
      localStorage.setItem('colorPickerHistory', JSON.stringify(newHistory));
      
      setColorPickerOpen(false);
    } catch (error) {
      console.error('Error saving color:', error);
      setColorPickerError('Failed to save color. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleColorPickerCancel = () => {
    setColorPickerOpen(false);
    setColorPickerError('');
  };

  const handleColorPickerChange = (newColor: string) => {
    setColorPickerValue(newColor);
    setColorPickerError('');
  };

  const handleColorFromHistory = (color: string) => {
    setColorPickerValue(color);
    setColorPickerError('');
  };

  const getColorName = (hexColor: string): string => {
    const colorMap: { [key: string]: string } = {
      '#FF0000': 'Red',
      '#00FF00': 'Green',
      '#0000FF': 'Blue',
      '#FFFF00': 'Yellow',
      '#FF00FF': 'Magenta',
      '#00FFFF': 'Cyan',
      '#000000': 'Black',
      '#FFFFFF': 'White',
      '#808080': 'Gray',
      '#FFA500': 'Orange',
      '#800080': 'Purple',
      '#008000': 'Dark Green',
      '#FFC0CB': 'Pink',
      '#A52A2A': 'Brown',
      '#FFD700': 'Gold',
    };
    return colorMap[hexColor.toUpperCase()] || 'Custom';
  };

  const getColorContrast = (hexColor: string): 'light' | 'dark' => {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? 'dark' : 'light';
  };

  // Preview Functions
  const openPreviewModal = (settings?: DealerContentSetting) => {
    setPreviewSettings(settings || dealerContentSettings);
    setPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewSettings(null);
  };

  const toggleInlinePreview = () => {
    setShowInlinePreview(!showInlinePreview);
  };

  // Get preview settings (combines current settings with any pending changes)
  const getPreviewSettings = (): DealerContentSetting => {
    if (!dealerContentSettings) {
      console.log('getPreviewSettings - dealerContentSettings is null/undefined');
      return {} as DealerContentSetting;
    }
    
    console.log('getPreviewSettings - dealerContentSettings:', dealerContentSettings);
    
    const baseSettings = { ...dealerContentSettings };
    
    // Apply any pending changes from edit modal
    if (editModalOpen && editModalData[editModalField]) {
      const currentField = baseSettings[editModalField as keyof DealerContentSetting] as any;
      baseSettings[editModalField as keyof DealerContentSetting] = {
        ...currentField,
        value: editModalData[editModalField],
      } as any;
    }
    
    // Apply any pending changes from color picker
    if (colorPickerOpen && colorPickerValue) {
      const currentField = baseSettings[colorPickerField as keyof DealerContentSetting] as any;
      baseSettings[colorPickerField as keyof DealerContentSetting] = {
        ...currentField,
        value: colorPickerValue,
      } as any;
    }
    
    console.log('getPreviewSettings - final baseSettings:', baseSettings);
    return baseSettings;
  };

  // Helper function to get setting value for preview
  const getPreviewSettingValue = (field: keyof DealerContentSetting, settings: DealerContentSetting): string => {
    const setting = settings[field] as any;
    const value = setting?.value || '';
    
    // Debug logging for address and phone specifically
    if (field === 'address' || field === 'phone') {
      console.log(`getPreviewSettingValue - ${field}:`, { setting, value });
    }
    
    return value;
  };

  // Email Preview Component
  const EmailPreview: React.FC<{ settings: DealerContentSetting }> = ({ settings }) => {
    const getValue = (field: keyof DealerContentSetting) => getPreviewSettingValue(field, settings);
    
    // Debug logging to help identify the issue
    React.useEffect(() => {
      console.log('EmailPreview - settings:', settings);
      console.log('EmailPreview - address value:', getValue('address'));
      console.log('EmailPreview - phone value:', getValue('phone'));
    }, [settings]);
    
    // Load Google Fonts if needed
    React.useEffect(() => {
      const loadGoogleFonts = () => {
        const fonts = [
          getValue('content_font'),
          getValue('disclaimer_font'),
        ].filter(Boolean);
        
        fonts.forEach(font => {
          if (font && !font.includes(',')) {
            const fontName = font.trim();
            const googleFonts = ['Roboto', 'Open Sans', 'Lato', 'Poppins', 'Montserrat', 'Source Sans Pro'];
            
            if (googleFonts.includes(fontName)) {
              const link = document.createElement('link');
              link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`;
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          }
        });
      };
      
      loadGoogleFonts();
    }, [settings]);
    
    // Helper function to ensure proper font formatting with fallbacks
    const formatFont = (fontValue: string, defaultFont: string = 'Arial, sans-serif') => {
      if (!fontValue || fontValue.trim() === '') return defaultFont;
      
      // If it's already a complete font stack, return as is
      if (fontValue.includes(',')) return fontValue;
      
      // If it's just a font name, add fallbacks
      const fontName = fontValue.trim();
      const fallbacks = {
        'Arial': 'Arial, Helvetica, sans-serif',
        'Roboto': 'Roboto, Arial, sans-serif',
        'Open Sans': '"Open Sans", Arial, sans-serif',
        'Helvetica': 'Helvetica, Arial, sans-serif',
        'Georgia': 'Georgia, serif',
        'Times New Roman': '"Times New Roman", Times, serif',
        'Verdana': 'Verdana, Geneva, sans-serif',
        'Tahoma': 'Tahoma, Geneva, sans-serif',
        'Trebuchet MS': '"Trebuchet MS", Arial, sans-serif',
        'Impact': 'Impact, Charcoal, sans-serif',
        'Comic Sans MS': '"Comic Sans MS", cursive, sans-serif',
        'Courier New': '"Courier New", Courier, monospace',
        'Lucida Console': '"Lucida Console", Monaco, monospace',
        'Palatino': 'Palatino, "Palatino Linotype", serif',
        'Garamond': 'Garamond, serif',
        'Bookman': 'Bookman, serif',
        'Avant Garde': '"Avant Garde", Arial, sans-serif',
        'Arial Black': '"Arial Black", Arial, sans-serif',
        'Arial Narrow': '"Arial Narrow", Arial, sans-serif',
        'Century Gothic': '"Century Gothic", Arial, sans-serif',
        'Franklin Gothic': '"Franklin Gothic", Arial, sans-serif',
        'Gill Sans': '"Gill Sans", Arial, sans-serif',
        'Lucida Sans': '"Lucida Sans", Arial, sans-serif',
        'MS Sans Serif': '"MS Sans Serif", Arial, sans-serif',
        'MS Serif': '"MS Serif", serif',
        'Symbol': 'Symbol, serif',
        'Wingdings': 'Wingdings, serif',
      };
      
      return fallbacks[fontName] || `${fontName}, Arial, sans-serif`;
    };
    
    const contentFont = formatFont(getValue('content_font'), 'Arial, sans-serif');
    const contentFontSize = getValue('content_font_size') || '16';
    const contentFontColor = getValue('content_font_color') || theme.palette.text.primary;
    const primaryHeadlineFontColor = getValue('primary_headline_font_color') || theme.palette.primary.main;
    const primaryHeadlineSize = getValue('primary_headline_size') || '22';
    const headlineHighlightFontColor = getValue('headline_highlight_font_color') || theme.palette.primary.main;
    const headlineHighlightFontSize = getValue('headline_highlight_font_size') || '18';
    const disclaimerFont = formatFont(getValue('disclaimer_font'), 'Arial, sans-serif');
    const disclaimerFontColor = getValue('disclaimer_font_color') || theme.palette.text.secondary;
    const disclaimerFontSize = getValue('disclaimer_font_size') || '14';
    const primaryCtaBgColor = getValue('primary_cta_button_background_color') || theme.palette.primary.main;
    const primaryCtaFontColor = getValue('primary_cta_button_font_color') || theme.palette.primary.contrastText;
    const primaryCtaFontSize = getValue('primary_cta_button_font_size') || '16';
    const primaryCtaBorderSize = getValue('primary_cta_button_border_size') || '1';
    const primaryCtaBorderColor = getValue('primary_cta_button_border_color') || theme.palette.primary.main;
    const secondaryCtaBgColor = getValue('secondary_cta_button_background_color') || theme.palette.background.paper;
    const secondaryCtaFontColor = getValue('secondary_cta_button_font_color') || theme.palette.primary.main;
    const secondaryCtaFontSize = getValue('secondary_cta_button_font_size') || '14';
    const secondaryCtaBorderSize = getValue('secondary_cta_button_border_size') || '1';
    const secondaryCtaBorderColor = getValue('secondary_cta_button_border_color') || theme.palette.primary.main;
    const logoUrl = getValue('logo_bar_url') || '';
    
    // Get address and phone values directly without fallbacks
    const address = getValue('address');
    const phone = getValue('phone');
    
    // Debug logging for address and phone
    console.log('EmailPreview - Final address value:', address);
    console.log('EmailPreview - Final phone value:', phone);
    const disclaimerText = getValue('standard_disclaimer_text') || 'Terms and conditions apply. Subject to availability.';
    const ctaButtons = settings.cta_buttons || [];

    return (
      <Box
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          bgcolor: theme.palette.background.card,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme.shadows[4],
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Dealer Logo" 
                style={{ 
                  height: 40, 
                  maxWidth: 120,
                  objectFit: 'contain'
                }} 
              />
            ) : (
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 40, height: 40 }}>
                <Settings />
              </Avatar>
            )}
            <Typography
              variant="h6"
              sx={{
                fontFamily: contentFont,
                fontSize: `${primaryHeadlineSize}px`,
                color: primaryHeadlineFontColor,
                fontWeight: 700,
              }}
            >
              {selectedDealer.name || 'Dealership Name'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Arial, sans-serif',
                fontSize: `${contentFontSize}px`,
                color: contentFontColor,
                fontWeight: 600,
              }}
            >
              {phone}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Arial, sans-serif',
                fontSize: `${disclaimerFontSize}px`,
                color: contentFontColor,
              }}
            >
              {address}
            </Typography>
          </Box>
        </Box>

        {/* Email Content */}
        <Box sx={{ 
          p: 3,
        }}>
          {/* Subject Line */}
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Arial, sans-serif',
              fontSize: `${headlineHighlightFontSize}px`,
              color: headlineHighlightFontColor,
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
            }}
          >
            Special Offer Just for You!
          </Typography>

          {/* Main Content */}
          <Typography
            variant="body1"
            sx={{
              fontFamily: contentFont,
              fontSize: `${contentFontSize}px`,
              color: contentFontColor,
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Dear Valued Customer,
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: contentFont,
              fontSize: `${contentFontSize}px`,
              color: contentFontColor,
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            We're excited to offer you an exclusive deal on our latest inventory that we've carefully curated just for our valued customers like you. Our team has worked tirelessly to bring you the best selection of vehicles with unbeatable prices and financing options that fit your budget perfectly.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: contentFont,
              fontSize: `${contentFontSize}px`,
              color: contentFontColor,
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Whether you're looking for a reliable family car, a sleek sedan, or a powerful SUV, we have something special waiting for you. Don't miss out on these incredible savings that are available for a limited time only!
          </Typography>



          {/* CTA Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Available CTA Buttons */}
            {ctaButtons.map((button, index) => {
              const isPrimary = index === 0;
              return (
                <ButtonMAIQ
                  key={button.id}
                  variant={isPrimary ? "contained" : "outlined"}
                  fullWidth
                  sx={{
                    ...(isPrimary ? {
                      backgroundColor: primaryCtaBgColor,
                      color: primaryCtaFontColor,
                      border: `${primaryCtaBorderSize}px solid ${primaryCtaBorderColor}`,
                      fontFamily: getValue('primary_cta_button_font') || 'inherit',
                      fontSize: `${primaryCtaFontSize}px`,
                      '&:hover': {
                        backgroundColor: primaryCtaBgColor,
                        opacity: 0.9,
                      },
                    } : {
                      backgroundColor: secondaryCtaBgColor,
                      color: secondaryCtaFontColor,
                      border: `${secondaryCtaBorderSize}px solid ${secondaryCtaBorderColor}`,
                      fontFamily: getValue('secondary_cta_button_font') || 'inherit',
                      fontSize: `${secondaryCtaFontSize}px`,
                      '&:hover': {
                        backgroundColor: secondaryCtaBgColor,
                        opacity: 0.9,
                      },
                    }),
                  }}
                >
                  {button.name}
                </ButtonMAIQ>
              );
            })}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.background.card || theme.palette.grey[50],
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: disclaimerFont,
                fontSize: `${disclaimerFontSize}px`,
                color: disclaimerFontColor,
                lineHeight: 1.4,
                display: 'block',
                textAlign: 'center',
              }}
            >
              {disclaimerText}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Open Add Modal
  const openAddCtaModal = () => {
    setCtaModalMode('add');
    setCtaModalData({ id: '', name: '', url: '', type: 'SRP', tags: [] });
    setCtaModalOpen(true);
  };

  // Open Edit Modal
  const openEditCtaModal = (button: CTAButton) => {
    setCtaModalMode('edit');
    // Ensure tags is always an array when editing
    setCtaModalData({
      ...button,
      tags: button.tags || [] // Initialize as empty array if not present
    });
    setCtaModalOpen(true);
  };

  // Handle CTA Modal Save
  const handleCtaModalSave = async () => {
    if (!dealerContentSettings) return;

    setCtaSubmitting(true);
    try {
      if (ctaModalMode === 'add') {
        const newButton = { ...ctaModalData, id: Date.now().toString() };
        await addCtaButton(selectedDealerId, newButton);
      } else {
        await updateCtaButton(selectedDealerId, ctaModalData);
      }

      setCtaModalOpen(false);
    } catch (error) {
      console.error('Error saving CTA buttons:', error);
    } finally {
      setCtaSubmitting(false);
    }
  };

  // Handle CTA Modal Cancel
  const handleCtaModalCancel = () => {
    setCtaModalOpen(false);
  };

  // Handle CTA Delete
  const handleCtaDelete = async (buttonId: string) => {
    if (!dealerContentSettings) return;

    try {
      await deleteCtaButton(selectedDealerId, buttonId);
    } catch (error) {
      console.error('Error deleting CTA button:', error);
    }
  };

  // Create default dealer content settings
  const handleCreateDefaultSettings = async () => {
    setIsSubmitting(true);
    try {
      const dealerName = selectedDealer.name || selectedDealer.abbreviation;
      const defaultSettings = getDefaultDealerContentSettings(selectedDealerId, dealerName);
      if (defaultSettings) {
        const settingsWithDealer = {
          ...defaultSettings,
          dealer: selectedDealerId,
        };
        await createDealerContentSettings(settingsWithDealer);
      }
    } catch (error) {
      console.error('Error creating default dealer content settings:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if dealer is selected but no data exists
  if (selectedDealerId && !loading && (!dealerContentSettings || Object.keys(dealerContentSettings).length === 0)) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100%',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            bgcolor: 'grey.100',
            borderRadius: 2,
            boxShadow: 0,
            border: '1px solid',
            borderColor: 'grey.200',
            p: { xs: 1.5, md: 2.5 },
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: 1.2,
              textAlign: 'center',
              fontSize: '1.15rem',
            }}
          >
            Welcome to Dealer Content Settings
          </Typography>
          
          {/* Dealer Name Display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1,
            }}
          >
            <SettingsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontSize: '1.1rem',
              }}
            >
              {selectedDealer.name || 'Selected Dealer'}
            </Typography>
          </Box>
          
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              mb: 2,
              textAlign: 'center',
              maxWidth: 420,
              fontSize: '0.95rem',
            }}
          >
            Configure content settings for <strong>{selectedDealer.name || 'this dealer'}</strong> for the first time. Default values will be automatically set and you can customize them later to match your dealership's branding and preferences.
          </Typography>
          
          <Box sx={{ width: '100%', mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
                textAlign: 'center',
                fontSize: '0.98rem',
              }}
            >
              What you'll be able to configure for {selectedDealer.name || 'this dealer'}:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                {
                  icon: <StarIcon fontSize="small" sx={{ color: 'primary.main' }} />,
                  title: 'Basic Settings',
                  desc: 'Configure logo URLs, contact information, and disclaimer text.',
                },
                {
                  icon: <TouchAppIcon fontSize="small" color="primary" />,
                  title: 'Call to Action Buttons',
                  desc: 'Add custom buttons with your dealership URLs for quotes, financing, and more.',
                },
                {
                  icon: <FormatColorTextIcon fontSize="small" sx={{ color: 'primary.main' }} />,
                  title: 'Branding & Colors',
                  desc: 'Set your brand colors, fonts, and styling for consistent messaging.',
                },
              ].map((feature, idx) => (
                <Box
                  key={feature.title}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 28,
                      minHeight: 28,
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: 2,
                      boxShadow: 0,
                      flexShrink: 0,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color:
                          idx === 0
                            ? 'primary.main'
                            : idx === 1
                            ? 'primary.main'
                            : 'text.primary',
                        mb: 0.2,
                        fontSize: '0.98rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.4,
                        fontSize: '0.91rem',
                        wordBreak: 'break-word',
                      }}
                    >
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Call to Action Buttons - Matching App Theme */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            {isSubmitting ? (
              <Skeleton 
                variant="rounded" 
                width={280} 
                height={40} 
                sx={{ borderRadius: 1 }}
              />
            ) : (
              <ButtonMAIQ
                variant="contained"
                color="primary"
                onClick={handleCreateDefaultSettings}
                disabled={isSubmitting}
                startIcon={<SettingsIcon />}
              >
                {`Configure for ${selectedDealer.name || 'Dealer'}`}
              </ButtonMAIQ>
            )}
            
            <Typography
              variant="caption"
              sx={{
                color: 'text.primary',
                fontSize: '0.85rem',
                textAlign: 'center',
              }}
            >
              This will set up default content settings for {selectedDealer.name || 'this dealer'}
            </Typography>
          </Box>

          {/* Help Text */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.primary',
              mt: 2.5,
              textAlign: 'center',
              display: 'block',
              fontSize: '0.91rem',
            }}
          >
            Don't worry! You can always edit these settings later. Default values will give you a great starting point.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!dealerContentSettings) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.primary">
          Please select a dealer to view content settings.
        </Typography>
      </Box>
    );
  }

  // Helper function to get setting value
  const getSettingValue = (field: keyof DealerContentSetting): string => {
    const setting = dealerContentSettings[field] as any;
    return setting?.value || '';
  };

  const getBasicSettingRowValue = (row: BasicSettingRow): string => {
    if (row.kind === 'website') {
      return dealerContentSettings?.primary_contact?.website || '';
    }

    return getSettingValue(row.field);
  };

  const getBasicSettingEditField = (row: BasicSettingRow): string => {
    if (row.kind === 'website') {
      return 'harmonized_website';
    }
    return row.field;
  };

  // Helper function to get contact value
  const getContactValue = (contactType: 'primary_contact' | 'invoice_contact' | 'secondary_contact'): string => {
    if (!dealerContentSettings) return 'Not set';
    const contact = dealerContentSettings[contactType];
    if (!contact) return 'Not set';
    const parts: string[] = [];
    if (contact.name) parts.push(contact.name);
    else if (contact.first_name || contact.last_name) {
      parts.push([contact.first_name, contact.last_name].filter(Boolean).join(' '));
    }
    if (contact.title) parts.push(`(${contact.title})`);
    if (contact.email) parts.push(contact.email);
    if (contact.phone) parts.push(contact.phone);
    return parts.length > 0 ? parts.join(' - ') : 'Not set';
  };

  // Format time-of-day (minutes from midnight) using formatToDealerTime so it shows in dealer TZ
  const formatTimeInDealerTz = (minutesFromMidnight: number): string => {
    const tz = getDealerTimezoneFromStorage();
    const m = moment().tz(tz).startOf('day').add(minutesFromMidnight, 'minutes');
    const full = formatToDealerTime(m.toISOString());
    return full.replace(/^\d{2} \w{3} \d{4}, /, '').trim();
  };

  type DayOfServiceDisplay = {
    dayKey: string;
    label: string;
    timeLabel: string;
  };

  // Helper function to get days of service display (times via formatToDealerTime / dealer timezone)
  const getDaysOfServiceDisplay = (): DayOfServiceDisplay[] => {
    if (!dealerContentSettings?.days_of_service) return [];

    const days = dealerContentSettings.days_of_service;
    const activeDays: DayOfServiceDisplay[] = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    dayNames.forEach(day => {
      const dayData = days[day as keyof typeof days];

      if (dayData?.status) {
        const startTime =
          dayData.start_time !== undefined ? formatTimeInDealerTz(dayData.start_time) : '';
        const endTime =
          dayData.end_time !== undefined ? formatTimeInDealerTz(dayData.end_time) : '';

        const capitalizedDay = day.charAt(0).toUpperCase() + day.slice(1);

        activeDays.push({
          dayKey: day,
          label: capitalizedDay,
          timeLabel: startTime && endTime ? `${startTime} - ${endTime}` : '',
        });
      }
    });

    return activeDays;
  };

  // Handle multi make selection (persist make[] only; clear manufacturer)
  const handleMakesChange = (makes: MakeItem[]) => {
    if (!dealerContentSettings || !selectedDealerId) return;

    const updatePayload: Partial<DealerContentSetting> = {
      make: makes.map(make => make.make_id),
      manufacturer: null,
    };

    updateDealerContentSettings(selectedDealerId, updatePayload);
  };

  const isMakeOptionDisabled = (make: MakeItem): boolean => {
    if (!dealerContentSettings || makeSegmentsLoading) {
      return true;
    }
    return makeSegmentsAvailability[String(make.make_id).trim()] !== true;
  };

  // Helper function to get marketing data display
  const getMarketingDataDisplay = (): string => {
    if (!dealerContentSettings?.marketing_data) return 'Not configured';
    const md = dealerContentSettings.marketing_data;
    const parts: string[] = [];
    if (md.engine_mode) parts.push(`Engine: ${md.engine_mode}`);
    if (md.nurturing?.status) parts.push('Nurturing: Active');
    if (md.conquest?.conquest_per_month) parts.push(`Conquest: ${md.conquest.conquest_per_month}/month`);
    if (md.scan?.scans_per_month) parts.push(`Scans: ${md.scan.scans_per_month}/month`);
    return parts.length > 0 ? parts.join(', ') : 'Configured';
  };

  // Helper function to get setting type
  const getSettingType = (field: keyof DealerContentSetting): string => {
    const setting = dealerContentSettings[field] as any;
    return setting?.type || 'text';
  };

  // Helper function to format display value based on field type
  const getDisplayValue = (field: keyof DealerContentSetting): string => {
    const value = getSettingValue(field);
    const type = getSettingType(field);
    
    if (type === 'border-size' || type === 'font-size') {
      return `${value}px`;
    }
    
    return value;
  };

  // Helper function to format any value based on field name and type
  const formatValueForDisplay = (value: string, fieldName: string, fieldType?: string): string => {
    const type = fieldType || getSettingType(fieldName as keyof DealerContentSetting);
    
    if (type === 'border-size' || type === 'font-size') {
      return `${value}px`;
    }
    
    return value;
  };

  // Helper function to format font name for display
  const formatFontNameForDisplay = (fontValue: string): string => {
    if (!fontValue) return '';
    
    // Extract the primary font name (before the first comma)
    const primaryFont = fontValue.split(',')[0].trim();
    
    // Remove quotes if present
    return primaryFont.replace(/['"]/g, '');
  };

  // Helper function to get font family for display
  const getFontFamilyForDisplay = (fontValue: string): string => {
    if (!fontValue) return 'Arial, sans-serif';
    
    // If it's already a complete font stack, return as is
    if (fontValue.includes(',')) return fontValue;
    
    // If it's just a font name, add fallbacks
    const fontName = fontValue.trim();
    const fallbacks = {
      'Arial': 'Arial, Helvetica, sans-serif',
      'Roboto': 'Roboto, Arial, sans-serif',
      'Open Sans': '"Open Sans", Arial, sans-serif',
      'Helvetica': 'Helvetica, Arial, sans-serif',
      'Georgia': 'Georgia, serif',
      'Times New Roman': '"Times New Roman", Times, serif',
      'Verdana': 'Verdana, Geneva, sans-serif',
      'Tahoma': 'Tahoma, Geneva, sans-serif',
      'Trebuchet MS': '"Trebuchet MS", Arial, sans-serif',
      'Impact': 'Impact, Charcoal, sans-serif',
      'Comic Sans MS': '"Comic Sans MS", cursive, sans-serif',
      'Courier New': '"Courier New", Courier, monospace',
      'Lucida Console': '"Lucida Console", Monaco, monospace',
      'Palatino': 'Palatino, "Palatino Linotype", serif',
      'Garamond': 'Garamond, serif',
      'Bookman': 'Bookman, serif',
      'Avant Garde': '"Avant Garde", Arial, sans-serif',
      'Arial Black': '"Arial Black", Arial, sans-serif',
      'Arial Narrow': '"Arial Narrow", Arial, sans-serif',
      'Century Gothic': '"Century Gothic", Arial, sans-serif',
      'Franklin Gothic': '"Franklin Gothic", Arial, sans-serif',
      'Gill Sans': '"Gill Sans", Arial, sans-serif',
      'Lucida Sans': '"Lucida Sans", Arial, sans-serif',
      'MS Sans Serif': '"MS Sans Serif", Arial, sans-serif',
      'MS Serif': '"MS Serif", serif',
      'Symbol': 'Symbol, serif',
      'Wingdings': 'Wingdings, serif',
    };
    
    return fallbacks[fontName] || `${fontName}, Arial, sans-serif`;
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.paper, p: 3 }}>
      {error && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            background: theme.palette.error.light,
            color: theme.palette.error.dark,
            borderRadius: 1,
          }}
        >
          {error}
        </Box>
      )}


      {/* Basic Settings */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: theme.shadows[2],
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Basic Settings
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => openPreviewModal(dealerContentSettings)}
            
          >
            Preview Email
          </ButtonMAIQ>
        </Box>
        <Grid container spacing={2}>
          {BASIC_SETTINGS_ROWS.map((row) => {
            const fieldKey = getBasicSettingEditField(row);
            const label = row.label;
            const value = getBasicSettingRowValue(row);

            return (
            <Grid item xs={12} key={fieldKey}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  p: 2,
                  bgcolor: theme.palette.background.card || theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {value.length > 100 ? `${value.substring(0, 100)}...` : value || 'Not set'}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => openEditModal(fieldKey, label, value)}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Edit />
                </IconButton>
              </Box>
            </Grid>
            );
          })}

          {/* Primary Contact */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                >
                  Primary Contact
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {getContactValue('primary_contact')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setContactModalType('primary');
                  setContactModalData(dealerContentSettings?.primary_contact || {});
                  setContactModalOpen(true);
                }}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>

          {/* Invoice Contact */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                >
                  Invoice Contact
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {getContactValue('invoice_contact')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setContactModalType('invoice');
                  setContactModalData(dealerContentSettings?.invoice_contact || {});
                  setContactModalOpen(true);
                }}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>

          {/* Secondary Contact */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                >
                  Secondary Contact
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {getContactValue('secondary_contact')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setContactModalType('secondary');
                  setContactModalData(dealerContentSettings?.secondary_contact || {});
                  setContactModalOpen(true);
                }}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>

          {/* Days of Service */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                >
                  Days of Service
                </Typography>
                {(() => {
                  const daysDisplay = getDaysOfServiceDisplay();

                  if (!daysDisplay.length) {
                    return (
                      <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.disabled }}
                      >
                        Not configured
                      </Typography>
                    );
                  }

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      {daysDisplay.map(day => (
                        <Box
                          key={day.dayKey}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            columnGap: 1,
                            rowGap: 0.25,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              minWidth: 100,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {day.label}
                          </Typography>
                          {day.timeLabel && (
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.primary }}
                            >
                              {day.timeLabel}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setDaysOfServiceData(dealerContentSettings?.days_of_service || {});
                  setDaysOfServiceModalOpen(true);
                }}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>

          {/* Makes */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 1 }}
                >
                  Makes
                </Typography>
                {makesLoading ? (
                  <Skeleton variant="text" width="60%" height={24} />
                ) : availableMakes.length === 0 ? (
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    No makes available
                  </Typography>
                ) : (
                  <HeaderMakeSelect
                    multiple
                    makesList={availableMakes}
                    selectedMakes={selectedMakeItems}
                    loading={makesLoading || makeSegmentsLoading}
                    disabled={!dealerContentSettings || makesLoading}
                    onChange={handleMakesChange}
                    isOptionDisabled={isMakeOptionDisabled}
                    disabledOptionTooltip="MyAutoIQ segment settings are not available for this make"
                    emptyLabel="Select makes"
                  />
                )}
              </Box>
            </Box>
          </Grid>

          {/* Marketing Data */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                >
                  Marketing Data
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  {getMarketingDataDisplay()}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setMarketingData(dealerContentSettings?.marketing_data || {});
                  setMarketingDataModalOpen(true);
                  setHouseholdIncomeError('');
                  setPurchasingPowerError('');
                }}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>

          {/* Timezone */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: theme.palette.background.card || theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 1 }}
                >
                  Timezone
                </Typography>
                {timezonesLoading ? (
                  <Skeleton variant="text" width="60%" height={24} />
                ) : timezones.length === 0 ? (
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    No timezones available
                  </Typography>
                ) : (
                  <HeaderSearchableSelectMAIQ
                    id="dealer-timezone-select"
                    value={dealerContentSettings?.time_zone || ''}
                    options={timezoneOptions}
                    onChange={timezone => {
                      if (timezone !== dealerContentSettings?.time_zone) {
                        handleTimezoneChange(timezone);
                      }
                    }}
                    disabled={!dealerContentSettings || timezonesLoading}
                    loading={timezonesLoading}
                    emptyLabel="Select Timezone"
                    searchPlaceholder="Search timezones..."
                    emptySearchLabel="No timezones found"
                    emptyOptionsLabel="No timezones available"
                    aria-label="Select timezone"
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Call To Action Buttons */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: theme.shadows[2],
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TouchAppIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Call To Action Buttons
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ButtonMAIQ
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => openPreviewModal(dealerContentSettings)}
              
            >
              Preview Email
            </ButtonMAIQ>
            <ButtonMAIQ
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openAddCtaModal}
              sx={isInAIAvatarLayout ? {
                backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
                color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[200],
                },
              } : undefined}
            >
              Add New
            </ButtonMAIQ>
          </Box>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.primary.main, height: 48 }}>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText || 'white',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 0,
                    pl: 3,
                    minWidth: 120,
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText || 'white',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 0,
                    minWidth: 200,
                  }}
                >
                  URL
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText || 'white',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 0,
                    minWidth: 100,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText || 'white',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 0,
                    minWidth: 150,
                  }}
                >
                  Tags
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText || 'white',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 0,
                    textAlign: 'right',
                    pr: 3,
                    minWidth: 80,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(dealerContentSettings.cta_buttons || []).map((button, idx) => (
                <TableRow
                  key={button.id}
                  sx={{
                    '&:hover': { bgcolor: theme.palette.action.hover },
                    height: 48,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      pl: 3,
                      verticalAlign: 'middle',
                      border: 0,
                    }}
                  >
                    <span
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => openEditCtaModal(button)}
                    >
                      {button.name}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: 15,
                      border: 0,
                      verticalAlign: 'middle',
                    }}
                  >
                    <Link
                      href={button.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.primary.dark,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {button.url}
                    </Link>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: 15,
                      border: 0,
                      verticalAlign: 'middle',
                    }}
                  >
                    {button.type || ''}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: 15,
                      border: 0,
                      verticalAlign: 'middle',
                      maxWidth: 150,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(button.tags || []).map((tag, tagIdx) => (
                        <Chip
                          key={tagIdx}
                          label={tag}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            '& .MuiChip-label': {
                              padding: '0 6px',
                            },
                          }}
                        />
                      ))}
                      {(!button.tags || button.tags.length === 0) && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>
                          No tags
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'right',
                      pr: 3,
                      border: 0,
                      verticalAlign: 'middle',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        sx={{ color: theme.palette.primary.main }}
                        onClick={() => openEditCtaModal(button)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: theme.palette.error.main }}
                        onClick={() => handleCtaDelete(button.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Branding - Message Content */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: theme.shadows[2],
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormatColorTextIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Branding - Message Content
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => openPreviewModal(dealerContentSettings)}
          >
            Preview Email
          </ButtonMAIQ>
        </Box>
        <Grid container spacing={2}>
          {[
            { field: 'content_font', label: 'Content Font' },
            { field: 'content_font_color', label: 'Content Font Color' },
            { field: 'content_font_size', label: 'Content Font Size' },
            {
              field: 'primary_headline_font_color',
              label: 'Primary Headline Font Color',
            },
            { field: 'primary_headline_size', label: 'Primary Headline Size' },
            {
              field: 'headline_highlight_font_color',
              label: 'Headline Highlight Font Color',
            },
            {
              field: 'headline_highlight_font_size',
              label: 'Headline Highlight Font Size',
            },
            { field: 'disclaimer_font', label: 'Disclaimer Font' },
            { field: 'disclaimer_font_color', label: 'Disclaimer Font Color' },
            { field: 'disclaimer_font_size', label: 'Disclaimer Font Size' },
          ].map(({ field, label }) => (
            <Grid item xs={12} md={6} key={field}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: theme.palette.background.card || theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSettingType(field as keyof DealerContentSetting) ===
                      'color' && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: getSettingValue(
                            field as keyof DealerContentSetting,
                          ),
                        }}
                      />
                    )}
                    {(field === 'content_font' || field === 'disclaimer_font') ? (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.text.primary,
                          fontFamily: getFontFamilyForDisplay(getSettingValue(field as keyof DealerContentSetting)),
                          fontWeight: 500,
                        }}
                      >
                        {formatFontNameForDisplay(getSettingValue(field as keyof DealerContentSetting))}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                        {getDisplayValue(field as keyof DealerContentSetting)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    openEditModal(
                      field,
                      label,
                      getSettingValue(field as keyof DealerContentSetting),
                    )
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Edit />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Branding - Primary CTA Buttons */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: theme.shadows[2],
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Branding - Primary CTA Buttons
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => openPreviewModal(dealerContentSettings)}
          >
            Preview Email
          </ButtonMAIQ>
        </Box>
        <Grid container spacing={2}>
          {[
            {
              field: 'primary_cta_button_background_color',
              label: 'Primary CTA Button Background Color',
            },
            {
              field: 'primary_cta_button_border_size',
              label: 'Primary CTA Button Border Size',
            },
            {
              field: 'primary_cta_button_border_color',
              label: 'Primary CTA Button Border Color',
            },
            {
              field: 'primary_cta_button_font',
              label: 'Primary CTA Button Font',
            },
            {
              field: 'primary_cta_button_font_color',
              label: 'Primary CTA Button Font Color',
            },
            {
              field: 'primary_cta_button_font_size',
              label: 'Primary CTA Button Font Size',
            },
          ].map(({ field, label }) => (
            <Grid item xs={12} md={6} key={field}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: theme.palette.background.card || theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSettingType(field as keyof DealerContentSetting) ===
                      'color' && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: getSettingValue(
                            field as keyof DealerContentSetting,
                          ),
                        }}
                      />
                    )}
                    {field === 'primary_cta_button_font' ? (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.text.primary,
                          fontFamily: getFontFamilyForDisplay(getSettingValue(field as keyof DealerContentSetting)),
                          fontWeight: 500,
                        }}
                      >
                        {formatFontNameForDisplay(getSettingValue(field as keyof DealerContentSetting))}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                        {getDisplayValue(field as keyof DealerContentSetting)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    openEditModal(
                      field,
                      label,
                      getSettingValue(field as keyof DealerContentSetting),
                    )
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Edit />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Branding - Secondary CTA Buttons */}
      <Paper
        sx={{
          p: 3,
          boxShadow: theme.shadows[2],
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Branding - Secondary CTA Buttons
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => openPreviewModal(dealerContentSettings)}
          >
            Preview Email
          </ButtonMAIQ>
        </Box>
        <Grid container spacing={2}>
          {[
            {
              field: 'secondary_cta_button_background_color',
              label: 'Secondary CTA Button Background Color',
            },
            {
              field: 'secondary_cta_button_border_size',
              label: 'Secondary CTA Button Border Size',
            },
            {
              field: 'secondary_cta_button_border_color',
              label: 'Secondary CTA Button Border Color',
            },
            {
              field: 'secondary_cta_button_font',
              label: 'Secondary CTA Button Font',
            },
            {
              field: 'secondary_cta_button_font_color',
              label: 'Secondary CTA Button Font Color',
            },
            {
              field: 'secondary_cta_button_font_size',
              label: 'Secondary CTA Button Font Size',
            },
          ].map(({ field, label }) => (
            <Grid item xs={12} md={6} key={field}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: theme.palette.background.card || theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSettingType(field as keyof DealerContentSetting) ===
                      'color' && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: getSettingValue(
                            field as keyof DealerContentSetting,
                          ),
                        }}
                      />
                    )}
                    {field === 'secondary_cta_button_font' ? (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.text.primary,
                          fontFamily: getFontFamilyForDisplay(getSettingValue(field as keyof DealerContentSetting)),
                          fontWeight: 500,
                        }}
                      >
                        {formatFontNameForDisplay(getSettingValue(field as keyof DealerContentSetting))}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                        {getDisplayValue(field as keyof DealerContentSetting)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    openEditModal(
                      field,
                      label,
                      getSettingValue(field as keyof DealerContentSetting),
                    )
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Edit />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>


      {/* Enhanced Edit Modal */}
      <ModalMAIQ
        open={editModalOpen}
        onClose={handleCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header - fixed */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box className="modal-header-icon">
                  {getFieldIcon(editModalField)}
                </Box>
                <Typography variant="h6" fontWeight="600">
                  {editModalTitle}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleCancel}
                    sx={{
                      p: 0.5,
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: 3,
                height: '100%'
              }}>
                <Box sx={{ 
                  flex: '0 0 50%',
                  overflowY: 'auto',
                  pr: 2
                }}>
                
                {/* Field Information */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Field Information
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                          Field Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {editModalTitle}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                          Field Type
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {editModalType || 'text'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                          {getFieldDescription(editModalField)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Current Value Display */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Current Value
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      minHeight: 60,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontFamily: editModalField.includes('font') ? 'monospace' : 'inherit',
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatValueForDisplay(editModalOriginalValue, editModalField) || 'No value set'}
                    </Typography>
                  </Box>
                </Box>

                {/* Input Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    New Value
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(editModalData).map(([key, value]) => (
                      <Box key={key}>
                        {editModalField.includes('font') && !editModalField.includes('font_size') ? (
                          <FormControl fullWidth size="small" error={!!editModalError}>
                            <InputLabel>Select Font</InputLabel>
                            <Select
                              value={value}
                              onChange={e => setEditModalData({ [key]: e.target.value })}
                              label="Select Font"
                              sx={{
                                '& .MuiSelect-select': {
                                  fontFamily: value || 'Arial, sans-serif',
                                },
                              }}
                            >
                              <MenuItem value="Arial, sans-serif" sx={{ fontFamily: 'Arial, sans-serif' }}>
                                Arial
                              </MenuItem>
                              <MenuItem value="Roboto, sans-serif" sx={{ fontFamily: 'Roboto, sans-serif' }}>
                                Roboto
                              </MenuItem>
                              <MenuItem value="Open Sans, sans-serif" sx={{ fontFamily: 'Open Sans, sans-serif' }}>
                                Open Sans
                              </MenuItem>
                              <MenuItem value="Helvetica, Arial, sans-serif" sx={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
                                Helvetica
                              </MenuItem>
                              <MenuItem value="Georgia, serif" sx={{ fontFamily: 'Georgia, serif' }}>
                                Georgia
                              </MenuItem>
                              <MenuItem value="Times New Roman, serif" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                Times New Roman
                              </MenuItem>
                              <MenuItem value="Verdana, sans-serif" sx={{ fontFamily: 'Verdana, sans-serif' }}>
                                Verdana
                              </MenuItem>
                              <MenuItem value="Tahoma, sans-serif" sx={{ fontFamily: 'Tahoma, sans-serif' }}>
                                Tahoma
                              </MenuItem>
                              <MenuItem value="Trebuchet MS, Arial, sans-serif" sx={{ fontFamily: 'Trebuchet MS, Arial, sans-serif' }}>
                                Trebuchet MS
                              </MenuItem>
                              <MenuItem value="Impact, Charcoal, sans-serif" sx={{ fontFamily: 'Impact, Charcoal, sans-serif' }}>
                                Impact
                              </MenuItem>
                              <MenuItem value="Comic Sans MS, cursive, sans-serif" sx={{ fontFamily: 'Comic Sans MS, cursive, sans-serif' }}>
                                Comic Sans MS
                              </MenuItem>
                              <MenuItem value="Courier New, Courier, monospace" sx={{ fontFamily: 'Courier New, Courier, monospace' }}>
                                Courier New
                              </MenuItem>
                              <MenuItem value="Lucida Console, Monaco, monospace" sx={{ fontFamily: 'Lucida Console, Monaco, monospace' }}>
                                Lucida Console
                              </MenuItem>
                              <MenuItem value="Palatino, Palatino Linotype, serif" sx={{ fontFamily: 'Palatino, Palatino Linotype, serif' }}>
                                Palatino
                              </MenuItem>
                              <MenuItem value="Garamond, serif" sx={{ fontFamily: 'Garamond, serif' }}>
                                Garamond
                              </MenuItem>
                              <MenuItem value="Bookman, serif" sx={{ fontFamily: 'Bookman, serif' }}>
                                Bookman
                              </MenuItem>
                              <MenuItem value="Avant Garde, Arial, sans-serif" sx={{ fontFamily: 'Avant Garde, Arial, sans-serif' }}>
                                Avant Garde
                              </MenuItem>
                              <MenuItem value="Arial Black, Arial, sans-serif" sx={{ fontFamily: 'Arial Black, Arial, sans-serif' }}>
                                Arial Black
                              </MenuItem>
                              <MenuItem value="Arial Narrow, Arial, sans-serif" sx={{ fontFamily: 'Arial Narrow, Arial, sans-serif' }}>
                                Arial Narrow
                              </MenuItem>
                              <MenuItem value="Century Gothic, Arial, sans-serif" sx={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
                                Century Gothic
                              </MenuItem>
                              <MenuItem value="Franklin Gothic, Arial, sans-serif" sx={{ fontFamily: 'Franklin Gothic, Arial, sans-serif' }}>
                                Franklin Gothic
                              </MenuItem>
                              <MenuItem value="Gill Sans, Arial, sans-serif" sx={{ fontFamily: 'Gill Sans, Arial, sans-serif' }}>
                                Gill Sans
                              </MenuItem>
                              <MenuItem value="Lucida Sans, Arial, sans-serif" sx={{ fontFamily: 'Lucida Sans, Arial, sans-serif' }}>
                                Lucida Sans
                              </MenuItem>
                            </Select>
                            {editModalError && (
                              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5 }}>
                                {editModalError}
                              </Typography>
                            )}
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            value={value}
                            onChange={e => setEditModalData({ [key]: e.target.value })}
                            placeholder={getFieldPlaceholder(editModalField)}
                            multiline={
                              key.includes('Message') ||
                              key.includes('Disclaimer') ||
                              key.includes('standard_disclaimer_text') ||
                              key.includes('address')
                            }
                            rows={
                              key.includes('Message') ||
                              key.includes('Disclaimer') ||
                              key.includes('standard_disclaimer_text')
                                ? 4
                                : key.includes('address')
                                ? 3
                                : 1
                            }
                            variant="outlined"
                            size="small"
                            error={!!editModalError}
                            helperText={editModalError}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                padding: '12px 16px',
                                fontFamily: editModalField.includes('font') ? 'monospace' : 'inherit',
                              },
                              '& .MuiInputLabel-root': {
                                fontWeight: 600,
                              },
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Inline Preview Section */}
                {showInlinePreview && (
                  <Box sx={{ bgcolor: theme.palette.background.paper }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Live Preview
                    </Typography>
                    <Box sx={{ 
                      border: `1px solid ${theme.palette.divider}`, 
                      borderRadius: 2, 
                      p: 2, 
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      <EmailPreview settings={getPreviewSettings()} />
                    </Box>
                  </Box>
                )}

                {/* Quick Suggestions */}
                {editModalField.includes('font') && !editModalField.includes('font_size') && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Popular Font Choices
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {[
                        'Arial, sans-serif',
                        'Roboto, sans-serif',
                        'Open Sans, sans-serif',
                        'Helvetica, Arial, sans-serif',
                        'Georgia, serif',
                        'Times New Roman, serif',
                        'Verdana, sans-serif',
                        'Tahoma, sans-serif'
                      ].map((font) => (
                        <Chip
                          key={font}
                          label={font.split(',')[0]}
                          onClick={() => setEditModalData({ [editModalField]: font })}
                          sx={{
                            cursor: 'pointer',
                            fontFamily: font,
                            '&:hover': {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText || 'white',
                            },
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Font Size Quick Options */}
                {(editModalField.includes('font_size') || editModalField.includes('headline_size')) && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Quick Font Size Options
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['12', '14', '16', '18', '20', '22', '24', '28', '32'].map((size) => (
                        <Chip
                          key={size}
                          label={`${size}px`}
                          onClick={() => setEditModalData({ [editModalField]: size })}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText || 'white',
                            },
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Border Size Quick Options */}
                {editModalField.includes('border_size') && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Quick Border Size Options
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['0', '1', '2', '3', '4', '5', '6', '8', '10'].map((size) => (
                        <Chip
                          key={size}
                          label={`${size}px`}
                          onClick={() => setEditModalData({ [editModalField]: size })}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText || 'white',
                            },
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Color Quick Options */}
                {editModalField.includes('font_color') && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Quick Color Options
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {[
                        { color: '#000000', name: 'Black' },
                        { color: '#333333', name: 'Dark Gray' },
                        { color: '#666666', name: 'Gray' },
                        { color: '#999999', name: 'Light Gray' },
                        { color: '#ffffff', name: 'White' },
                        { color: '#0f284c', name: 'Navy Blue' },
                        { color: '#007bff', name: 'Blue' },
                        { color: '#28a745', name: 'Green' },
                        { color: '#ffc107', name: 'Yellow' },
                        { color: '#fd7e14', name: 'Orange' },
                        { color: '#dc3545', name: 'Red' },
                        { color: '#6f42c1', name: 'Purple' }
                      ].map((colorOption) => (
                        <Chip
                          key={colorOption.color}
                          label={colorOption.name}
                          onClick={() => setEditModalData({ [editModalField]: colorOption.color })}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: colorOption.color,
                            color: colorOption.color === '#ffffff' ? theme.palette.text.primary : theme.palette.common.white,
                            border: colorOption.color === '#ffffff' ? `1px solid ${theme.palette.divider}` : 'none',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}



                {/* Validation Status */}
                {editModalError && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.warning.light,
                      border: `1px solid ${theme.palette.warning.main}`,
                      borderRadius: 2,
                      borderLeft: `4px solid ${theme.palette.warning.dark}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.warning.dark, fontWeight: 500 }}>
                      ⚠️ {editModalError}
                    </Typography>
                  </Box>
                )}

                </Box>

                {/* Inline Preview Section - Always Show */}
                <Box sx={{ 
                  flex: '0 0 50%',
                  overflowY: 'auto',
                  pl: 2
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Live Preview
                  </Typography>
                  <Box sx={{ 
                    border: `1px solid ${theme.palette.divider}`, 
                    borderRadius: 2, 
                    p: 2, 
                    bgcolor: theme.palette.background.card || theme.palette.grey[50],
                    height: '100%',
                    overflowY: 'auto'
                  }}>
                    <EmailPreview settings={getPreviewSettings()} />
                  </Box>
                </Box>

              </Box>
            </Box>

            {/* Action Buttons - fixed at bottom */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ButtonMAIQ
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </ButtonMAIQ>
                {isSubmitting ? (
                  <Skeleton 
                    variant="rounded" 
                    width={140} 
                    height={36} 
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <ButtonMAIQ
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isSubmitting || !hasChanges()}
                    startIcon={<Save />}
                  >
                    Save Changes
                  </ButtonMAIQ>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* CTA Add/Edit Modal - User Management Style */}
      <ModalMAIQ
        open={ctaModalOpen}
        onClose={handleCtaModalCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header - fixed */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  {ctaModalMode === 'add'
                    ? 'Add New CTA Button'
                    : 'Edit CTA Button'}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleCtaModalCancel}
                    sx={{
                      p: 0.5,
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Name *
                  </Typography>
                  <TextField
                    fullWidth
                    value={ctaModalData.name}
                    onChange={e =>
                      setCtaModalData(d => ({ ...d, name: e.target.value }))
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '8px 14px',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    URL *
                  </Typography>
                  <TextField
                    fullWidth
                    value={ctaModalData.url}
                    onChange={e =>
                      setCtaModalData(d => ({ ...d, url: e.target.value }))
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '8px 14px',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Type *
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={ctaModalData.type || ''}
                      onChange={e =>
                        setCtaModalData(d => ({ ...d, type: e.target.value as 'SRP' | 'FORM' }))
                      }
                      displayEmpty
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: '8px 14px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Type
                      </MenuItem>
                      <MenuItem value="SRP">SRP</MenuItem>
                      <MenuItem value="FORM">FORM</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Tags Selector */}
                <Box>
                  <TagSelector
                    value={ctaModalData.tags || []}
                    onChange={(tags) => setCtaModalData(d => ({ ...d, tags }))}
                    tagType="CTA_BUTTON"
                    label="Tags *"
                    placeholder="Search existing tags or create new ones..."
                    disabled={ctaSubmitting}
                    maxTags={5}
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons - fixed at bottom */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <ButtonMAIQ
                variant="outlined"
                onClick={handleCtaModalCancel}
                disabled={ctaSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {ctaSubmitting ? (
                <Skeleton 
                  variant="rounded" 
                  width={80} 
                  height={36} 
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <ButtonMAIQ
                  variant="contained"
                  color="primary"
                  onClick={handleCtaModalSave}
                  disabled={
                    ctaSubmitting || !ctaModalData.name || !ctaModalData.url || !ctaModalData.type || !ctaModalData.tags || ctaModalData.tags.length === 0
                  }
                  startIcon={<Save />}
                >
                  Save
                </ButtonMAIQ>
              )}
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Enhanced Color Picker Modal */}
      <ModalMAIQ
        open={colorPickerOpen}
        onClose={handleColorPickerCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormatColorTextIcon className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  {colorPickerTitle}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleColorPickerCancel}
                    sx={{
                      p: 0.5,
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: 3,
                height: '100%'
              }}>
                <Box sx={{ 
                  flex: '0 0 50%',
                  overflowY: 'auto',
                  pr: 2
                }}>
                
                {/* Current Color Preview */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Current Color
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      bgcolor: theme.palette.background.card || theme.palette.grey[50],
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 60,
                        borderRadius: 2,
                        border: `2px solid ${theme.palette.divider}`,
                        bgcolor: colorPickerValue || theme.palette.background.paper,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {colorPickerValue && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: getColorContrast(colorPickerValue) === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
                            fontWeight: 600,
                            textShadow: theme.palette.mode === 'dark' ? `0 1px 2px ${theme.palette.common.black}` : `0 1px 2px rgba(0,0,0,0.3)`,
                            fontSize: '0.7rem',
                          }}
                        >
                          {getColorName(colorPickerValue)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                        {colorPickerValue || 'No color selected'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.primary' }}>
                        {colorPickerValue ? `${getColorName(colorPickerValue)} • ${getColorContrast(colorPickerValue)} background` : 'Select a color below'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Color Input Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Select Color
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ position: 'relative' }}>
                        <input
                          type="color"
                          value={colorPickerValue}
                          onChange={(e) => handleColorPickerChange(e.target.value)}
                          style={{
                            width: '60px',
                            height: '50px',
                            border: `2px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.primary' }}>
                          Picker
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          value={colorPickerValue}
                          onChange={(e) => handleColorPickerChange(e.target.value)}
                          placeholder="#000000"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!colorPickerError}
                          helperText={colorPickerError}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontWeight: 600,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Quick Color Palette */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Quick Colors
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                      '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
                      '#000000', '#FFFFFF', '#808080', '#FFA500', '#800080', '#008000',
                      '#FFC0CB', '#A52A2A', '#FFD700', '#FF4500', '#32CD32', '#4169E1'
                    ].map((color) => (
                      <Tooltip key={color} title={getColorName(color)}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: color,
                            border: `2px solid ${theme.palette.divider}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              borderColor: theme.palette.primary.main,
                            },
                            ...(colorPickerValue === color && {
                              borderColor: theme.palette.primary.main,
                              borderWidth: '3px',
                            }),
                          }}
                          onClick={() => handleColorPickerChange(color)}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                {/* Color History */}
                {colorPickerHistory.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Recently Used
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {colorPickerHistory.slice(0, 8).map((color) => (
                        <Tooltip key={color} title={`${getColorName(color)} - ${color}`}>
                          <Box
                            sx={{
                              width: 35,
                              height: 35,
                              borderRadius: 1,
                              bgcolor: color,
                              border: `2px solid ${theme.palette.divider}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                borderColor: theme.palette.primary.main,
                              },
                              ...(colorPickerValue === color && {
                                borderColor: theme.palette.primary.main,
                                borderWidth: '3px',
                              }),
                            }}
                            onClick={() => handleColorFromHistory(color)}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Color Information */}
                {colorPickerValue && validateColor(colorPickerValue) && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Color Information
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: theme.palette.background.card || theme.palette.grey[50],
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[100]}`,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Color Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getColorName(colorPickerValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Hex Code
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {colorPickerValue}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Contrast
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {getColorContrast(colorPickerValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Type
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getColorName(colorPickerValue) !== 'Custom' ? 'Standard' : 'Custom'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                )}

                </Box>

                {/* Inline Preview Section for Color Picker - Always Show */}
                <Box sx={{ 
                  flex: '0 0 50%',
                  overflowY: 'auto',
                  pl: 2
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Live Preview
                  </Typography>
                  <Box sx={{ 
                    border: `1px solid ${theme.palette.divider}`, 
                    borderRadius: 2, 
                    p: 2, 
                    bgcolor: theme.palette.background.card || theme.palette.grey[50],
                    height: '100%',
                    overflowY: 'auto'
                  }}>
                    <EmailPreview settings={getPreviewSettings()} />
                  </Box>
                </Box>

              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ButtonMAIQ
                  variant="outlined"
                  onClick={handleColorPickerCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </ButtonMAIQ>
                {isSubmitting ? (
                  <Skeleton 
                    variant="rounded" 
                    width={120} 
                    height={36} 
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <ButtonMAIQ
                    variant="contained"
                    color="primary"
                    onClick={handleColorPickerSave}
                    disabled={isSubmitting || !validateColor(colorPickerValue)}
                    startIcon={<Save />}
                  >
                    Save Color
                  </ButtonMAIQ>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Email Preview Modal */}
      <ModalMAIQ
        open={previewModalOpen}
        onClose={closePreviewModal}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '90vw',
            maxWidth: '1200px',
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  Email Preview - {selectedDealer.name || 'Dealer'}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={closePreviewModal}
                    sx={{
                      p: 0.5,
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: theme.palette.background.default }}>
              {previewSettings && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Font Preview Section */}
                  <Paper sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Font Settings Preview
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                        Content: {getPreviewSettingValue('content_font', previewSettings) || 'Arial, sans-serif'} | 
                        Disclaimer: {getPreviewSettingValue('disclaimer_font', previewSettings) || 'Arial, sans-serif'}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Content Font
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontFamily: getPreviewSettingValue('content_font', previewSettings) || 'Arial, sans-serif',
                              fontSize: `${getPreviewSettingValue('content_font_size', previewSettings) || '16'}px`,
                              color: getPreviewSettingValue('content_font_color', previewSettings) || theme.palette.text.primary,
                              fontWeight: 500,
                            }}
                          >
                            Sample Content Text
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Disclaimer Font
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: getPreviewSettingValue('disclaimer_font', previewSettings) || 'Arial, sans-serif',
                              fontSize: `${getPreviewSettingValue('disclaimer_font_size', previewSettings) || '14'}px`,
                              color: getPreviewSettingValue('disclaimer_font_color', previewSettings) || theme.palette.text.secondary,
                            }}
                          >
                            Sample Disclaimer Text
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Primary Headline
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontFamily: getPreviewSettingValue('content_font', previewSettings) || 'Arial, sans-serif',
                              fontSize: `${getPreviewSettingValue('primary_headline_size', previewSettings) || '22'}px`,
                              color: getPreviewSettingValue('primary_headline_font_color', previewSettings) || theme.palette.primary.main,
                              fontWeight: 700,
                            }}
                          >
                            Sample Headline
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            Highlight Headline
                          </Typography>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontFamily: getPreviewSettingValue('content_font', previewSettings) || 'Arial, sans-serif',
                              fontSize: `${getPreviewSettingValue('headline_highlight_font_size', previewSettings) || '18'}px`,
                              color: getPreviewSettingValue('headline_highlight_font_color', previewSettings) || theme.palette.primary.main,
                              fontWeight: 700,
                            }}
                          >
                            Sample Highlight
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {/* Email Preview */}
                  <EmailPreview settings={previewSettings} />
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  This preview shows how your email will look with the current brand settings
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ButtonMAIQ
                  variant="outlined"
                  onClick={closePreviewModal}
                >
                  Close Preview
                </ButtonMAIQ>
              </Box>
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Contact Modal */}
      <ModalMAIQ
        open={contactModalOpen}
        onClose={handleContactModalCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  Edit {contactModalType === 'primary' ? 'Primary' : contactModalType === 'invoice' ? 'Invoice' : 'Secondary'} Contact
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleContactModalCancel}
                    sx={{ p: 0.5 }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Copy from Primary Contact Button - Only show for Invoice and Secondary */}
                {(contactModalType === 'invoice' || contactModalType === 'secondary') && dealerContentSettings?.primary_contact && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: theme.palette.background.card || theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Copy values from Primary Contact
                    </Typography>
                    <ButtonMAIQ
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy fontSize="small" />}
                      onClick={handleCopyFromPrimaryContact}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                    >
                      Copy
                    </ButtonMAIQ>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={contactModalData.name || ''}
                    onChange={e => setContactModalData(d => ({ ...d, name: e.target.value }))}
                    variant="outlined"
                    size="small"
                    placeholder="Full name"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                      First Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={contactModalData.first_name || ''}
                      onChange={e => setContactModalData(d => ({ ...d, first_name: e.target.value }))}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Last Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={contactModalData.last_name || ''}
                      onChange={e => setContactModalData(d => ({ ...d, last_name: e.target.value }))}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Title
                  </Typography>
                  <TextField
                    fullWidth
                    value={contactModalData.title || ''}
                    onChange={e => setContactModalData(d => ({ ...d, title: e.target.value }))}
                    variant="outlined"
                    size="small"
                    placeholder="Job title"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={contactModalData.email || ''}
                    onChange={e => setContactModalData(d => ({ ...d, email: e.target.value }))}
                    variant="outlined"
                    size="small"
                    placeholder="email@example.com"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Phone
                  </Typography>
                  <TextField
                    fullWidth
                    value={contactModalData.phone || ''}
                    onChange={e => setContactModalData(d => ({ ...d, phone: e.target.value }))}
                    variant="outlined"
                    size="small"
                    placeholder="+1234567890"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Address
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={contactModalData.address || ''}
                    onChange={e => setContactModalData(d => ({ ...d, address: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Website
                  </Typography>
                  <TextField
                    fullWidth
                    value={contactModalData.website || ''}
                    onChange={e => setContactModalData(d => ({ ...d, website: e.target.value }))}
                    variant="outlined"
                    size="small"
                    placeholder="https://example.com"
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <ButtonMAIQ
                variant="outlined"
                onClick={handleContactModalCancel}
                disabled={contactSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {contactSubmitting ? (
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
              ) : (
                <ButtonMAIQ
                  variant="contained"
                  color="primary"
                  onClick={handleContactModalSave}
                  disabled={contactSubmitting}
                  startIcon={<Save />}
                >
                  Save
                </ButtonMAIQ>
              )}
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Days of Service Modal */}
      <ModalMAIQ
        open={daysOfServiceModalOpen}
        onClose={handleDaysOfServiceModalCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '700px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Edit className="modal-header-icon" fontSize="small" />
                  <Typography variant="h6" fontWeight="600">
                    Edit Days of Service
                  </Typography>
                </Box>
                {(() => {
                  const dealerTz = getDealerTimezoneFromStorage();
                  return dealerTz ? (
                    <Typography variant="caption" color="text.secondary">
                      Times in dealer timezone ({dealerTz})
                    </Typography>
                  ) : null;
                })()}
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleDaysOfServiceModalCancel}
                    sx={{ p: 0.5 }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => {
                  const dayData = daysOfServiceData[day as keyof typeof daysOfServiceData] || {};
                  const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                  return (
                    <Box
                      key={day}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 100 }}>
                          {dayName}
                        </Typography>
                        <FormControl>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                              type="checkbox"
                              checked={dayData.status || false}
                              onChange={(e) => {
                                setDaysOfServiceData(d => ({
                                  ...d,
                                  [day]: { ...d[day as keyof typeof d], status: e.target.checked },
                                }));
                              }}
                            />
                            <Typography variant="body2">Open</Typography>
                          </Box>
                        </FormControl>
                      </Box>
                      {dayData.status && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Start Time
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={dayData.start_time !== undefined ? Math.floor(dayData.start_time / 60) : ''}
                                onChange={(e) => {
                                  const selectedHour = e.target.value;
                                  if (selectedHour !== '' && selectedHour !== null) {
                                    // Always set minutes to 0
                                    const totalMinutes = Number(selectedHour) * 60;
                                    setDaysOfServiceData(d => ({
                                      ...d,
                                      [day]: { ...d[day as keyof typeof d], start_time: totalMinutes },
                                    }));
                                  } else {
                                    setDaysOfServiceData(d => ({
                                      ...d,
                                      [day]: { ...d[day as keyof typeof d], start_time: undefined },
                                    }));
                                  }
                                }}
                                displayEmpty
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    padding: '8px 14px',
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Hour</em>
                                </MenuItem>
                                {Array.from({ length: 24 }, (_, i) => {
                                  const hour = i;
                                  const period = hour >= 12 ? 'PM' : 'AM';
                                  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                                  return (
                                    <MenuItem key={hour} value={hour}>
                                      {displayHour}:00 {period}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                              End Time
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={dayData.end_time !== undefined ? Math.floor(dayData.end_time / 60) : ''}
                                onChange={(e) => {
                                  const selectedHour = e.target.value;
                                  if (selectedHour !== '' && selectedHour !== null) {
                                    // Always set minutes to 0
                                    const totalMinutes = Number(selectedHour) * 60;
                                    setDaysOfServiceData(d => ({
                                      ...d,
                                      [day]: { ...d[day as keyof typeof d], end_time: totalMinutes },
                                    }));
                                  } else {
                                    setDaysOfServiceData(d => ({
                                      ...d,
                                      [day]: { ...d[day as keyof typeof d], end_time: undefined },
                                    }));
                                  }
                                }}
                                displayEmpty
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    padding: '8px 14px',
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Hour</em>
                                </MenuItem>
                                {Array.from({ length: 24 }, (_, i) => {
                                  const hour = i;
                                  const period = hour >= 12 ? 'PM' : 'AM';
                                  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                                  return (
                                    <MenuItem key={hour} value={hour}>
                                      {displayHour}:00 {period}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <ButtonMAIQ
                variant="outlined"
                onClick={handleDaysOfServiceModalCancel}
                disabled={daysOfServiceSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {daysOfServiceSubmitting ? (
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
              ) : (
                <ButtonMAIQ
                  variant="contained"
                  color="primary"
                  onClick={handleDaysOfServiceModalSave}
                  disabled={daysOfServiceSubmitting}
                  startIcon={<Save />}
                >
                  Save
                </ButtonMAIQ>
              )}
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Marketing Data Modal */}
      <ModalMAIQ
        open={marketingDataModalOpen}
        onClose={handleMarketingDataModalCancel}
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            className="modal-header"
            sx={{
              p: 2,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit className="modal-header-icon" fontSize="small" />
                <Typography variant="h6" fontWeight="600">
                  Edit Marketing Data
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    className="modal-header-button"
                    aria-label="close"
                    onClick={handleMarketingDataModalCancel}
                    sx={{ p: 0.5 }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Ideal Customer */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Ideal Customer
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Household Income
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.ideal_customer?.household_income || ''}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = value ? Number(value) : undefined;
                          
                          // Validation: between 50000 and 1000000
                          if (numValue !== undefined) {
                            if (numValue < 50000 || numValue > 1000000) {
                              setHouseholdIncomeError('Household income must be between $50,000 and $1,000,000');
                            } else {
                              setHouseholdIncomeError('');
                            }
                          } else {
                            setHouseholdIncomeError('');
                          }
                          
                          setMarketingData(d => ({
                            ...d,
                            ideal_customer: {
                              ...d.ideal_customer,
                              household_income: numValue,
                            },
                          }));
                        }}
                        variant="outlined"
                        size="small"
                        placeholder="Enter household income"
                        error={!!householdIncomeError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: theme.palette.text.secondary }}>$</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {householdIncomeError && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.error.main,
                            mt: 0.5,
                            display: 'block',
                          }}
                        >
                          {householdIncomeError}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Purchasing Power
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.ideal_customer?.purchasing_power || ''}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = value ? Number(value) : undefined;
                          
                          // Validation: between 459 and 820
                          if (numValue !== undefined) {
                            if (numValue < 459 || numValue > 820) {
                              setPurchasingPowerError('Purchasing power must be between 459 and 820');
                            } else {
                              setPurchasingPowerError('');
                            }
                          } else {
                            setPurchasingPowerError('');
                          }
                          
                          setMarketingData(d => ({
                            ...d,
                            ideal_customer: {
                              ...d.ideal_customer,
                              purchasing_power: numValue,
                            },
                          }));
                        }}
                        variant="outlined"
                        size="small"
                        placeholder="Enter purchasing power"
                        error={!!purchasingPowerError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: theme.palette.text.secondary }}>$</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {purchasingPowerError && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.error.main,
                            mt: 0.5,
                            display: 'block',
                          }}
                        >
                          {purchasingPowerError}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* ADF */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    ADF (Automated Data Feed)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Lead Expiry (days)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.adf?.lead_expiry || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          adf: {
                            ...d.adf,
                            lead_expiry: e.target.value ? Number(e.target.value) : undefined,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="Enter lead expiry days"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        CRM Email
                      </Typography>
                      <TextField
                        fullWidth
                        type="email"
                        value={marketingData.adf?.crm_email || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          adf: {
                            ...d.adf,
                            crm_email: e.target.value,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="crm@example.com"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Incoming Mailbox
                      </Typography>
                      <TextField
                        fullWidth
                        value={marketingData.adf?.incoming_mailbox || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          adf: {
                            ...d.adf,
                            incoming_mailbox: e.target.value,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="incoming@example.com"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Outgoing Mailbox
                      </Typography>
                      <TextField
                        fullWidth
                        value={marketingData.adf?.outgoing_mailbox || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          adf: {
                            ...d.adf,
                            outgoing_mailbox: e.target.value,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="outgoing@example.com"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Nurturing */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Nurturing
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="checkbox"
                          checked={marketingData.nurturing?.status || false}
                          onChange={(e) => setMarketingData(d => ({
                            ...d,
                            nurturing: {
                              ...d.nurturing,
                              status: e.target.checked,
                            },
                          }))}
                        />
                        <Typography variant="body2">Nurturing Status (Active)</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Nurturing Cycle (days)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.nurturing?.nurturing_cycle || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          nurturing: {
                            ...d.nurturing,
                            nurturing_cycle: e.target.value ? Number(e.target.value) : undefined,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="Enter cycle days"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={marketingData.nurturing?.long || false}
                            onChange={(e) => setMarketingData(d => ({
                              ...d,
                              nurturing: {
                                ...d.nurturing,
                                long: e.target.checked,
                              },
                            }))}
                          />
                          <Typography variant="body2">Long</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={marketingData.nurturing?.immediate || false}
                            onChange={(e) => setMarketingData(d => ({
                              ...d,
                              nurturing: {
                                ...d.nurturing,
                                immediate: e.target.checked,
                              },
                            }))}
                          />
                          <Typography variant="body2">Immediate</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={marketingData.nurturing?.test_mode || false}
                            onChange={(e) => setMarketingData(d => ({
                              ...d,
                              nurturing: {
                                ...d.nurturing,
                                test_mode: e.target.checked,
                              },
                            }))}
                          />
                          <Typography variant="body2">Test Mode</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={marketingData.nurturing?.nurture_exist_lead || false}
                            onChange={(e) => setMarketingData(d => ({
                              ...d,
                              nurturing: {
                                ...d.nurturing,
                                nurture_exist_lead: e.target.checked,
                              },
                            }))}
                          />
                          <Typography variant="body2">Nurture Existing Lead</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Conquest */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Conquest
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Source
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={marketingData.conquest?.source || ''}
                          onChange={e => setMarketingData(d => ({
                            ...d,
                            conquest: {
                              ...d.conquest,
                              source: e.target.value,
                            },
                          }))}
                          displayEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '8px 14px',
                            },
                          }}
                        >
                          <MenuItem value="">Select Source</MenuItem>
                          <MenuItem value="All">All</MenuItem>
                          <MenuItem value="Used Car">Used Car</MenuItem>
                          <MenuItem value="New Car">New Car</MenuItem>
                          <MenuItem value="CPO">CPO</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Conquest Per Month
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.conquest?.conquest_per_month || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          conquest: {
                            ...d.conquest,
                            conquest_per_month: e.target.value ? Number(e.target.value) : undefined,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="Enter conquest per month"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Scan */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Scan
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Source
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={marketingData.scan?.source || ''}
                          onChange={e => setMarketingData(d => ({
                            ...d,
                            scan: {
                              ...d.scan,
                              source: e.target.value,
                            },
                          }))}
                          displayEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '8px 14px',
                            },
                          }}
                        >
                          <MenuItem value="">Select Source</MenuItem>
                          <MenuItem value="All">All</MenuItem>
                          <MenuItem value="ADF">ADF</MenuItem>
                          <MenuItem value="Month">Month</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Scans Per Month
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={marketingData.scan?.scans_per_month || ''}
                        onChange={e => setMarketingData(d => ({
                          ...d,
                          scan: {
                            ...d.scan,
                            scans_per_month: e.target.value ? Number(e.target.value) : undefined,
                          },
                        }))}
                        variant="outlined"
                        size="small"
                        placeholder="Enter scans per month"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Engine Mode */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Engine Mode
                  </Typography>
                  <TextField
                    fullWidth
                    value={marketingData.engine_mode || ''}
                    onChange={e => setMarketingData(d => ({
                      ...d,
                      engine_mode: e.target.value,
                    }))}
                    variant="outlined"
                    size="small"
                    placeholder="Enter engine mode"
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <ButtonMAIQ
                variant="outlined"
                onClick={handleMarketingDataModalCancel}
                disabled={marketingDataSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {marketingDataSubmitting ? (
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
              ) : (
                <ButtonMAIQ
                  variant="contained"
                  color="primary"
                  onClick={handleMarketingDataModalSave}
                  disabled={marketingDataSubmitting || !!householdIncomeError || !!purchasingPowerError}
                  startIcon={<Save />}
                >
                  Save
                </ButtonMAIQ>
              )}
            </Box>
          </Box>
        </Paper>
      </ModalMAIQ>
    </Box>
  );
};

export default DealerSettingsTab;
